import type { TemplateItem, TemplateLang, TemplateListRow } from '@/api/notificationTemplates';

/**
 * Правила экрана «Шаблоны уведомлений» без разметки (D45, договор
 * `docs/CONTRACTS/notification-templates.md`).
 */

/**
 * ⚠️ У СЕРВЕРА КАЗАХСКИЙ — `kz`, У ЛОКАЛЕЙ — `kk`. Это две разные буквы в двух
 * соседних системах, и перепутать их проще простого: экран молча покажет пустую
 * колонку или потеряет ключ перевода. Обе стороны названы здесь явно и сверяются
 * проверкой.
 */
export const SERVER_LANGS: readonly TemplateLang[] = ['ru', 'en', 'kz'];

const LOCALE_BY_SERVER: Record<TemplateLang, string> = { ru: 'ru', en: 'en', kz: 'kk' };

/** Код языка локалей по коду языка сервера: `kz` → `kk`. */
export function localeOf(lang: TemplateLang): string {
  return LOCALE_BY_SERVER[lang];
}

/** Код языка сервера по коду локали: `kk` → `kz`. Неизвестный — `undefined`. */
export function serverLangOf(locale: string): TemplateLang | undefined {
  const found = SERVER_LANGS.find((l) => LOCALE_BY_SERVER[l] === locale);
  return found;
}

/**
 * Ключ строки таблицы.
 *
 * ⚠️ ВАРИАНТ ВХОДИТ В КЛЮЧ. У «урок пройден» три текста: обычный, «ждёт оценки
 * куратора» и «без баллов»; у «сдана домашка» — два. Слить их в одну строку
 * значило бы пообещать баллы тому, кто их не получит.
 */
export function rowId(row: { kind: string; key: string; variant?: string }): string {
  return `${row.kind}|${row.key}|${row.variant ?? ''}`;
}

/** Состояние языка в строке; языка может не быть вовсе — тогда `undefined`. */
export function langState(row: TemplateListRow, lang: TemplateLang) {
  return row.langs.find((l) => l.lang === lang);
}

/**
 * Строки письма принадлежат МАКЕТУ, а не событию: у `02-access-granted-own-password`
 * события нет вовсе. В таблице их надо подписывать иначе, иначе админ будет
 * искать «событие 03-password-reset».
 */
export function isEmailLayoutRow(row: Pick<TemplateListRow, 'kind'>): boolean {
  return row.kind === 'email_texts';
}

const PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/** Все подстановки, встречающиеся в тексте. */
export function placeholdersIn(text: string): string[] {
  const out = new Set<string>();
  for (const m of text.matchAll(PLACEHOLDER)) out.add(m[1]);
  return [...out];
}

export type PlaceholderLint = {
  /** Обязательная подстановка потеряна — сервер откажет `template_placeholder_missing`. */
  missing: { field: string; name: string }[];
  /** Появилась подстановка, которой у события нет — уйдёт человеку буквами. */
  unknown: { field: string; name: string }[];
};

/**
 * Разбор подстановок в полях до сохранения.
 *
 * ⚠️ ЗАЧЕМ ПОВТОРЯТЬ ПРОВЕРКУ СЕРВЕРА. Сервер откажет и так, но узнавать о
 * потерянной `{{lesson_title}}` после нажатия «Сохранить» — значит терять
 * правку из виду: подсказка в поле показывает беду там же, где её сделали.
 *
 * Известными считаются подстановки из `placeholders` карточки: сервер присылает
 * их полем за полем. Поля, которых в `placeholders` нет вовсе, не проверяются —
 * у них подстановок не бывает.
 */
export function lintPlaceholders(item: TemplateItem, draft: Record<string, string>): PlaceholderLint {
  const missing: { field: string; name: string }[] = [];
  const unknown: { field: string; name: string }[] = [];

  for (const [field, required] of Object.entries(item.placeholders ?? {})) {
    const text = draft[field] ?? '';
    const present = placeholdersIn(text);
    for (const name of required) {
      if (!present.includes(name)) missing.push({ field, name });
    }
    for (const name of present) {
      if (!required.includes(name)) unknown.push({ field, name });
    }
  }
  return { missing, unknown };
}

/** Поле обязано остаться пустым (у письма нет пуша) — показываем заблокированным. */
export function isEmptyField(item: TemplateItem, field: string): boolean {
  return (item.empty_fields ?? []).includes(field);
}

/**
 * ⚠️ РУССКИЙ И АНГЛИЙСКИЙ — ЯКОРЯ ОТКАТА, и снятие галочки с них меняет текст не
 * одному языку, а всем, кто на них падает. Порядок отката: язык получателя (если
 * опубликован) → русский → английский → код. Снял русский — казахоязычные,
 * которым казахский ещё не открыт, поедут на английский; снял оба — все поедут
 * на текст из кода. Спрашиваем подтверждение.
 */
export function unpublishNeedsConfirm(lang: TemplateLang, next: boolean): boolean {
  return !next && (lang === 'ru' || lang === 'en');
}

/**
 * Коды отказов из §0 договора. Подсказка человеку — по коду, а не по тексту
 * сообщения: текст для человека и может меняться.
 */
export const TEMPLATE_CODES = [
  'template_placeholder_missing',
  'template_placeholder_unknown',
  'template_field_required',
  'template_field_must_be_empty',
  'email_text_required',
  'email_text_markup',
  'template_no_preview',
  'template_unknown_key',
  'email_text_unknown_key',
  'template_not_found',
  'template_stale',
  // Выключатель (§6а): критичное событие — ДЗ, оплата, доступ, чат, коды входа — не выключается.
  'template_critical',
] as const;

/** Выключатель события в удобном виде (§6а). */
export type EventSwitch = { disabled: boolean; canDisable: boolean; version: number };

/**
 * Выключатель строки списка или карточки.
 *
 * ⚠️ У СТРОК ПИСЬМА ВЫКЛЮЧАТЕЛЯ НЕТ: они принадлежат макету, а не событию. Сервер отдаёт им
 * `false`, `false`, `0` — повторяем это и здесь, даже если поля пришли другими, чтобы экран не
 * предложил выключить макет. Полей нет вовсе (сервер старый) — тоже «не выключено, нельзя».
 */
export function eventSwitch(row: {
  kind?: string;
  disabled?: boolean;
  can_disable?: boolean;
  switch_version?: number;
}): EventSwitch {
  if (row.kind !== 'template') return { disabled: false, canDisable: false, version: 0 };
  return {
    disabled: row.disabled === true,
    canDisable: row.can_disable === true,
    version: typeof row.switch_version === 'number' && row.switch_version >= 0 ? row.switch_version : 0,
  };
}

/**
 * Сколько СОБЫТИЙ выключено. Варианты одного события делят один выключатель, поэтому считаем
 * по ключу: «урок пройден» с тремя вариантами — одно выключенное событие, а не три.
 */
export function disabledEventCount(rows: { kind?: string; key: string; disabled?: boolean }[]): number {
  return new Set(rows.filter((r) => eventSwitch(r).disabled).map((r) => r.key)).size;
}

/**
 * События, у которых нет ограничения частоты: включишь — уходит каждый день.
 *
 * ⚠️ «Мы скучаем по вам!» уходил ежедневно, до 29 раз одному (техдолг п. 213); планировщик не
 * правили. Включение без предупреждения вернуло бы тот же поток.
 */
export const NO_FREQUENCY_LIMIT_EVENTS: ReadonlySet<string> = new Set(['student.inactive']);

/** Включение такого события требует предупреждения. Выключение — своё подтверждение, всегда. */
export function enableNeedsWarning(key: string, nextDisabled: boolean): boolean {
  return !nextDisabled && NO_FREQUENCY_LIMIT_EVENTS.has(key);
}

export const TEMPLATE_CODE_HINT_KEY: Record<string, string> = Object.fromEntries(
  TEMPLATE_CODES.map((c) => [c, `adminNotificationTemplates.code.${c}`]),
);

/** Правку и галочку отбило потому, что версию уже заменили. */
export function isStale(code: string | undefined): boolean {
  return code === 'template_stale';
}
